// ============================================
// INFRASTRUCTURE - Producto Repository
// ============================================

import { axiosClient } from './axios-client';
import type { Producto, PaginatedResponse, ProductoResumen, ProductoVendible, ProductoDestacado, InventarioProducto, InventarioValorizado, PreciosPromedio, SellableProductSlice } from '../../domain/entities';
import type {
  CreateProductoDto,
  UpdateProductoDto,
  ProductoFiltros,
  AddInventarioDto,
  UpdateInventarioDto,
  MermaDto,
  UpdateProductoAlmacenDto,
} from '../../domain/dtos';

export const productoRepository = {
  /**
   * Listar productos con filtros y paginación
   */
  async list(filtros?: ProductoFiltros): Promise<PaginatedResponse<Producto>> {
    const { data } = await axiosClient.get<PaginatedResponse<Producto>>('/productos', {
      params: filtros,
    });
    return data;
  },

  /**
   * Obtener un producto por ID
   */
  async getById(id: string): Promise<Producto> {
    const { data } = await axiosClient.get<Producto>(`/productos/${id}`);
    return data;
  },

  /**
   * Buscar productos (autocompletado)
   */
  async search(query: string): Promise<ProductoResumen[]> {
    const { data } = await axiosClient.get<ProductoResumen[]>('/productos/search', {
      params: { q: query },
    });
    return data;
  },

  /**
   * Escanear código de barras y obtener producto
   */
  async scanBarcode(scanBarCode: string): Promise<{
    id: number;
    codigo: string;
    nombre: string;
    marca: string;
    esRetail: boolean;
    posibleRetail: boolean;
    imagenUrl: string;
    imagenProductoId: number;
  }> {
    const { data } = await axiosClient.get(`/productos/scan-barcode/${scanBarCode}`);
    return data;
  },

  /**
   * Buscar imágenes de productos por nombre
   */
  async buscarImagenes(nombre: string): Promise<{
    id: number;
    descripcion: string;
    url: string;
    fuente: string;
  }[]> {
    const { data } = await axiosClient.get(`/imagenes-productos/${nombre}`);
    return data;
  },

  /**
   * Obtener productos vendibles con paginación
   */
  async getSellableProducts(
    page: number = 0,
    size: number = 20,
    searchTerm?: string,
    sort?: string,
    categoriaAlmacenId?: number,
    alerta?: string,
    soloPacks?: boolean
  ): Promise<PaginatedResponse<ProductoVendible>> {
    const params: Record<string, any> = { page, size };

    // Solo agregar searchTerm si tiene 3 o más caracteres
    if (searchTerm && searchTerm.length >= 3) {
      params.searchTerm = searchTerm;
    }

    // Agregar sort si existe
    if (sort) {
      params.sort = sort;
    }

    if (categoriaAlmacenId !== undefined) {
      params.categoriaAlmacenId = categoriaAlmacenId;
    }

    if (alerta) {
      params.alerta = alerta;
    }

    // Filtro "Solo packs" de Inventario (el backend ignora categoría y alerta en ese caso)
    if (soloPacks) {
      params.soloPacks = true;
    }

    const { data } = await axiosClient.get<PaginatedResponse<ProductoVendible>>('/products/sellable', {
      params,
    });
    return data;
  },

  /**
   * Buscador/escáner de productos del POS: liviano, sin COUNT(*) en el backend
   * (ver GET /products/sellable/search). No soporta filtro por categoría.
   */
  async searchSellableProducts(
    page: number = 0,
    size: number = 20,
    searchTerm?: string,
    sort?: string,
    excluirPacks?: boolean
  ): Promise<SellableProductSlice> {
    const params: Record<string, any> = { page, size };

    // Pestaña "Mis Productos" del POS: los packs se listan en la pestaña Packs
    if (excluirPacks) {
      params.excluirPacks = true;
    }

    if (searchTerm && searchTerm.length >= 3) {
      params.searchTerm = searchTerm;
    }

    if (sort) {
      params.sort = sort;
    }

    const { data } = await axiosClient.get<SellableProductSlice>('/products/sellable/search', {
      params,
    });
    return data;
  },

  /**
   * Pestaña "Packs" del POS: mismo contrato que searchSellableProducts pero solo packs
   * (GET /products/sellable/packs). Sin término lista todos los packs. `mode: 'barcode'` busca
   * el código exacto (escáner), también para códigos con letras como "P-LECHEGALLETA".
   */
  async searchSellablePacks(
    page: number = 0,
    size: number = 20,
    searchTerm?: string,
    sort?: string,
    mode?: 'barcode'
  ): Promise<SellableProductSlice> {
    const params: Record<string, any> = { page, size };

    if (mode) {
      params.mode = mode;
    }

    if (searchTerm && searchTerm.length >= 3) {
      params.searchTerm = searchTerm;
    }

    if (sort) {
      params.sort = sort;
    }

    const { data } = await axiosClient.get<SellableProductSlice>('/products/sellable/packs', {
      params,
    });
    return data;
  },

  /**
   * Total del catálogo activo del almacén (sin filtro de búsqueda). Pensado para pedirse
   * una sola vez, no en cada búsqueda — ver GET /products/sellable/count.
   */
  async countSellableProducts(): Promise<{ total: number }> {
    const { data } = await axiosClient.get<{ total: number }>('/products/sellable/count');
    return data;
  },

  /**
   * Obtener TODOS los productos vendibles (sin paginación) para exportar
   */
  async getAllSellableProducts(searchTerm?: string): Promise<ProductoVendible[]> {
    const params: Record<string, any> = {};
    if (searchTerm && searchTerm.length >= 3) {
      params.searchTerm = searchTerm;
    }
    const { data } = await axiosClient.get<ProductoVendible[]>('/products/sellable/all', { params });
    return data;
  },

  /**
   * Crear producto
   */
  async create(dto: CreateProductoDto): Promise<Producto> {
    const { data } = await axiosClient.post<Producto>('/productos', dto);
    return data;
  },

  /**
   * Actualizar producto
   */
  async update(id: string, dto: UpdateProductoDto): Promise<Producto> {
    const { data } = await axiosClient.patch<Producto>(`/productos/${id}`, dto);
    return data;
  },

  /**
   * Eliminar producto (soft delete)
   */
  async delete(id: string): Promise<void> {
    await axiosClient.delete(`/productos/${id}`);
  },

  /**
   * Ajustar stock
   */
  async adjustStock(id: string, cantidad: number, motivo: string): Promise<Producto> {
    const { data } = await axiosClient.post<Producto>(`/productos/${id}/stock`, {
      cantidad,
      motivo,
    });
    return data;
  },

  /**
   * Eliminar producto de almacén (inventario)
   */
  async deleteProductoAlmacen(productoAlmacenId: number): Promise<void> {
    await axiosClient.delete(`/productos-almacen/${productoAlmacenId}`);
  },

  /**
   * Obtener productos destacados (top vendidos y stock bajo)
   */
  async getTopSellingProducts(): Promise<ProductoDestacado[]> {
    const { data } = await axiosClient.get<ProductoDestacado[]>('/inventario/top-selling-products');
    return data;
  },

  /**
   * Obtener producto vendible por ID de almacén
   */
  async getProductoVendibleById(productoAlmacenId: number): Promise<ProductoVendible> {
    const { data } = await axiosClient.get<ProductoVendible>(`/products/sellable/${productoAlmacenId}`);
    return data;
  },

  /**
   * Obtener historial de inventario de un producto
   */
  async getInventarioProducto(productoAlmacenId: number): Promise<InventarioProducto[]> {
    const { data } = await axiosClient.get<InventarioProducto[]>(`/inventario/producto-almacen/${productoAlmacenId}`);
    return data;
  },

  /**
   * Añadir stock a un producto
   */
  async addInventario(dto: AddInventarioDto): Promise<InventarioProducto> {
    const { data } = await axiosClient.post<InventarioProducto>('/inventario', dto);
    return data;
  },

  /**
   * Actualizar registro de inventario
   */
  async updateInventario(inventarioId: number, dto: UpdateInventarioDto): Promise<InventarioProducto> {
    const { data } = await axiosClient.put<InventarioProducto>(`/inventario/${inventarioId}`, dto);
    return data;
  },

  /**
   * Eliminar registro de inventario
   */
  async deleteInventario(inventarioId: number): Promise<void> {
    await axiosClient.delete(`/inventario/${inventarioId}`);
  },

  /**
   * Registrar merma de un producto
   */
  async mermaProducto(dto: MermaDto): Promise<ProductoVendible> {
    const { data } = await axiosClient.post<ProductoVendible>('/mermas', dto);
    return data;
  },

  /**
   * Actualizar producto en almacén
   */
  async updateProductoAlmacen(productoAlmacenId: number, dto: UpdateProductoAlmacenDto): Promise<ProductoVendible> {
    const { data } = await axiosClient.patch<ProductoVendible>(`/productos-almacen/${productoAlmacenId}`, dto);
    return data;
  },

  /**
   * Agregar producto al almacén
   */
  async agregarProductoAlmacen(dto: {
    productoId: number | null;
    productoCodigo: string;
    productoNombre: string;
    imagenProductoId?: number;
    precioCompraActual: number;
    precioVentaActual: number;
    venderPor: 'U' | 'F';
    stock: number;
    fechaVencimiento: string | null;
    esPack: boolean;
  }): Promise<{
    productoAlmacenId: number;
    productoId: number;
    productoNombre: string;
    codigo: string;
    esPack: boolean;
    imagenUrl: string | null;
    precioCompraActual: number;
    precioVentaActual: number;
    esRetail: boolean;
    venderPor: string;
    stock: number;
  }> {
    const { data } = await axiosClient.post('/productos-almacen', dto);
    return data;
  },

  /**
   * Crear pack de productos
   */
  async crearPack(dto: {
    nombre: string;
    codigo?: string;
    precio: number;
    imagen?: string | null;
    items: Array<{
      productoHijoId: number;
      cantidad: number;
    }>;
    // true cuando el usuario ya confirmó que el precio supera la suma de precios
    // individuales (tras ver la advertencia PRECIO_CONFIRMACION_REQUERIDA)
    confirmado?: boolean;
  }): Promise<{
    productoAlmacenId: number;
    productoId: number;
    productoNombre: string;
    codigo: string;
    esPack: boolean;
    imagenUrl: string | null;
    precioCompraActual: number;
    precioVentaActual: number;
    esRetail: boolean;
    venderPor: string;
    stock: number;
  }> {
    const { data } = await axiosClient.post('/productos/packs', dto);
    return data;
  },

  async obtenerPack(productoAlmacenId: number): Promise<{
    packId: number;
    codigo: string;
    nombrePack: string;
    aliasNombre?: string | null;
    products: Array<{
      productoAlmacenId: number;
      nombre: string;
      quantity: number;
      precioNormalUnitario: number;
      precioCompraUnitario: number;
      discount: number;
      total: number;
    }>;
    precioRealTotal: number;
    precioPackTotal: number;
    ahorroTotal: number;
  }> {
    const { data } = await axiosClient.get(`/ventas/packs/${productoAlmacenId}`);
    return data;
  },

  async actualizarPack(dto: {
    productoAlmacenId: number;
    nombre: string;
    precio: number;
    imagenId?: number | null;
    items: Array<{
      productoHijoId: number;
      cantidad: number;
    }>;
    confirmado?: boolean;
  }): Promise<{
    productoAlmacenId: number;
    productoId: number;
    productoNombre: string;
    codigo: string;
    esPack: boolean;
    imagenUrl: string | null;
    precioCompraActual: number;
    precioVentaActual: number;
    esRetail: boolean;
    venderPor: string;
    stock: number;
  }> {
    const { data } = await axiosClient.put(`/productos/packs/${dto.productoAlmacenId}`, {
      nombre: dto.nombre,
      precio: dto.precio,
      imagenId: dto.imagenId,
      items: dto.items,
      confirmado: dto.confirmado ?? false,
    });
    return data;
  },

  /**
   * Obtener inventario valorizado con paginación
   */
  async getInventarioValorizado(page: number = 0, size: number = 20): Promise<InventarioValorizado> {
    const { data } = await axiosClient.get<InventarioValorizado>('/products/valued-inventory', {
      params: { page, size },
    });
    return data;
  },

  /**
   * Obtener precios promedio de un producto
   */
  async obtenerPreciosPromedio(productoId: number): Promise<PreciosPromedio> {
    const { data } = await axiosClient.get<PreciosPromedio>(`/productos/${productoId}/average-price`);
    return data;
  },

  /**
   * Establecer alias de nombre para un producto en el almacén
   */
  async setNombreProductoAlmacen(productoAlmacenId: number, nombre: string): Promise<void> {
    await axiosClient.put(`/productos-almacen/${productoAlmacenId}/nombre`, { nombre });
  },

  /**
   * Eliminar alias de nombre de un producto en el almacén
   */
  async deleteNombreProductoAlmacen(productoAlmacenId: number): Promise<void> {
    await axiosClient.delete(`/productos-almacen/${productoAlmacenId}/nombre`);
  },
};
